import React from 'react';
import { Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import HealWoundPopover from './HealWoundPopover';
import type { WoundResponse } from '../../services/api';
import { formatWoundDate, getWoundTypeLabel } from '../../constants/wounds';

interface WoundTableRowProps {
  wound: WoundResponse;
  assessmentCount: number;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}

const WoundTableRow: React.FC<WoundTableRowProps> = ({
  wound,
  assessmentCount,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
}) => {
  const isDiscontinued = !!wound.discontinued_date;

  return (
    <TableRow className={isDiscontinued ? 'opacity-60' : undefined}>
      <TableCell>
        {isDiscontinued ? (
          <Badge variant='secondary' className='text-xs'>
            Discontinued
          </Badge>
        ) : (
          <Badge
            variant='outline'
            className='text-xs bg-green-50 text-green-700 border-green-200'
          >
            Active
          </Badge>
        )}
      </TableCell>
      <TableCell className='text-sm'>
        <div className='space-y-0.5'>
          <p
            className={
              isDiscontinued ? 'italic text-muted-foreground' : 'font-medium'
            }
          >
            {wound.location}
          </p>
          <p className='text-xs text-muted-foreground'>
            {getWoundTypeLabel(wound.wound_type)}
          </p>
        </div>
      </TableCell>
      <TableCell className='text-sm text-muted-foreground'>
        {formatWoundDate(wound.onset_date)}
      </TableCell>
      <TableCell>
        <Button
          variant='outline'
          size='sm'
          onClick={() => onOpenAssessments(wound)}
        >
          Assessments ({assessmentCount})
        </Button>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-1.5'>
          {!isDiscontinued && canDiscontinue && (
            <HealWoundPopover
              minDate={new Date(wound.created_at)}
              onDiscontinue={(date, reason, comments) =>
                onDiscontinue(wound.id, date, reason, comments)
              }
              triggerLabel='Discontinue'
            />
          )}
          <Button variant='outline' size='sm' onClick={() => onEdit(wound)}>
            Edit
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => onDelete(wound)}
                    disabled={assessmentCount > 0}
                  >
                    <Trash2 className='h-4 w-4 text-destructive mr-1' />
                    Delete
                  </Button>
                </span>
              </TooltipTrigger>
              {assessmentCount > 0 && (
                <TooltipContent>
                  <p>Cannot delete a wound with assessments</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default WoundTableRow;
